import { useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Shared JANDI confirm / alert dialogs (center modal, portal-rendered on document.body).
 *
 * Screen rule: these are for confirmations and notifications only — Esc / overlay / button all
 * dismiss, and there is no form so Enter never submits. Block edit/add flows use JandiModal instead.
 * Styles live in ui-components/jandi-dialog.css (imported once in src/index.tsx).
 */

type ConfirmProps = {
    open: boolean;
    title: string;
    children: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

export const JandiConfirmDialog = ({
    open,
    title,
    children,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger,
    onConfirm,
    onCancel
}: ConfirmProps) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    return ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="jandi-dialog-overlay" onMouseDown={onCancel}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
            <div className="jandi-dialog" role="alertdialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
                <div className="jandi-dialog__header">
                    <span className="jandi-dialog__title">{title}</span>
                </div>
                <div className="jandi-dialog__body">{children}</div>
                <div className="jandi-dialog__footer">
                    <button type="button" className="jandi-dialog__btn jandi-dialog__btn--ghost" onClick={onCancel}>
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className={`jandi-dialog__btn ${danger ? 'jandi-dialog__btn--danger' : 'jandi-dialog__btn--primary'}`}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

type AlertProps = {
    open: boolean;
    title: string;
    children: React.ReactNode;
    closeLabel?: string;
    onClose: () => void;
};

export const JandiAlertDialog = ({ open, title, children, closeLabel = 'OK', onClose }: AlertProps) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="jandi-dialog-overlay" onMouseDown={onClose}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
            <div className="jandi-dialog" role="alertdialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
                <div className="jandi-dialog__header">
                    <span className="jandi-dialog__title">{title}</span>
                </div>
                <div className="jandi-dialog__body">{children}</div>
                <div className="jandi-dialog__footer">
                    <button type="button" className="jandi-dialog__btn jandi-dialog__btn--primary" onClick={onClose}>
                        {closeLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
